import type { ProductImageEntry } from "./types";

/** Native detail subjects: claims stop at the recorded read-only state. */
export const REMAINING_NATIVE_DETAIL_CATALOGUE: readonly ProductImageEntry[] = [
  {
    id: "planning.calendar.inset-flex-audit",
    module: "Kalender · Insatsflex för schemat",
    area: "schedule",
    opens: "Kalender → Granska insatsflex → Layer2InsetFlexAuditSheet",
    setup: [
      "Verifiera S2:s accepterade Carefox-kvitto och sparade flex-15-resultat via qualifiedReport",
      "Läs samma lösnings frysta OFF-policy med 50 nollminutsrader och tre sparade passintervall",
      "Öppna Granska insatsflex och expandera nollminutsraderna utan att ändra underlaget",
    ],
    scenario: "S2",
    visual: { variant: 5, focus: "audit" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Insatsflexgranskning med fryst grundpolicy utan extra flex och expanderade nollminutsrader",
        shows:
          "Den inbyggda granskningspanelen visar den frysta grundpolicyn Ingen extra flex och sparad proveniens för insatser med noll extra minuter.",
        proves:
          "Planeraren kan granska grundpolicyn för det accepterade Carefox-underlaget utan att ändra den. Detta visar OFF-policyns proveniens, inte ett positivt flexpåslag eller lösarkandidatens totala tidsfönster.",
      },
      en: {
        alt: "Visit flex audit with a frozen base policy of No extra flex and expanded zero-minute rows",
        shows:
          "The native audit panel shows the frozen base policy No extra flex and saved provenance for visit types with zero extra minutes.",
        proves:
          "The planner can inspect the accepted Carefox source's base policy without changing it. This records OFF-policy provenance, not a positive flex allowance or the solver candidate's complete time windows.",
      },
    },
  },
  {
    id: "planning.publication-history",
    module: "Publiceringshistorik",
    area: "operations",
    opens: "Drift → Publiceringshistorik → SchedulePublicationHistory",
    setup: [
      "Läs S3:s aktuella publicerade lösning och minst ett matchande schedulePublicationReceipts-kvitto",
      "Öppna drift för samma enhet och verksamhetsdag",
      "Expandera Publiceringshistorik och visa det aktuella kvittot",
    ],
    scenario: "S3",
    visual: { variant: 5, focus: "history" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Expanderad publiceringshistorik med aktuellt kvitto och tidpunkt",
        shows:
          "Den inbyggda historiken visar den publicerade versionens kvitto, status, tidpunkt och länk till lösningen.",
        proves:
          "Planeraren kan se vilken sparad version som publicerats och när. Ett publiceringskvitto visar inte att ett partnersystem har tagit emot planen.",
      },
      en: {
        alt: "Expanded publication history with the current receipt and timestamp",
        shows:
          "The native history shows the published version's receipt, status, timestamp and solution link.",
        proves:
          "The planner can identify the saved version that was published and when. A publication receipt does not establish delivery to a partner system.",
      },
    },
  },
  {
    id: "planning.optimization-setup",
    module: "Optimering · Inställningspanel",
    area: "schedule",
    opens:
      "/schedules/:id?optimize=1&solutionId=:solutionId → OptimizationSettingsSheet",
    setup: [
      "Verifiera S1:s sparade DEV-lösning samt befintliga pass och medarbetare",
      "Öppna den inbyggda inställningspanelen med den valda lösningen som bas",
      "Visa ScheduleCreateOrOptimizePanel och texten om ärvd insatsflex utan att starta optimering",
    ],
    scenario: "S1",
    visual: { variant: 5, focus: "settings" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Optimeringspanelen med vald baslösning och ärvd fryst insatsflex",
        shows:
          "Den inbyggda panelen visar Caires optimeringsförslag och hur insatsflex ärvs från schemats frysta policy.",
        proves:
          "Planeraren kan granska underlaget för en ny lösning från den valda rutten. Bilden visar panelens förberedande läge före start, inte ett optimeringsutfall.",
      },
      en: {
        alt: "Optimization panel with a selected base solution and inherited frozen visit flex",
        shows:
          "The native panel shows Caire's optimization proposal and explains that visit flex is inherited from the schedule's frozen policy.",
        proves:
          "The planner can inspect the setup for a new solution from the selected route. The image shows the panel before launch, not an optimization outcome.",
      },
    },
  },
  {
    id: "onboarding.organization-step",
    module: "Kom igång · Organisation",
    area: "standing-data",
    opens: "/start/organisation → OrganizationStep",
    setup: [
      "Läs S5:s befintliga Nacka-organisation; kräv sparat namn och kontorsadress utan platshållarvärde",
      "Öppna organisationssteget och verifiera att namn och adress kommer från samma sparade organisation",
      "Visa formuläret utan att ändra fält eller klicka Spara och fortsätt",
    ],
    scenario: "S5",
    visual: { variant: 5, focus: "organization-form" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Organisationssteget med befintligt namn och demoorganisationens sparade depåpunkt",
        shows:
          "Det inbyggda startformuläret visar demoorganisationens sparade namn, kontors-/depåpunkt och kontrollen Spara och fortsätt.",
        proves:
          "Planeraren kan granska organisationsuppgifterna där kontorsadressen används som grund för enheter och ruttplanering. Bilden visar ingen ny geokodning eller sparad ändring.",
      },
      en: {
        alt: "Organization setup step with an existing name and the demo organization's saved depot",
        shows:
          "The native start form displays the demo organization's saved name, office/depot point and Save and continue control.",
        proves:
          "The planner can review organization details where the office address supports units and route planning. The image records no new geocoding or saved change.",
      },
    },
  },
  {
    id: "ai.schedule-quick-prompts",
    module: "Fråga CAIRE · Schemafrågor",
    area: "ai",
    opens: "Kalender → Caire-assistent → CaireAiPanel",
    setup: [
      "Verifiera S1:s sparade DEV-lösning och öppna dess kalender",
      "Öppna assistentens chattläge med schema- och lösningskontext",
      "Kräv de fem inbyggda schemafrågorna från quickPrompts.ts och en tom skrivruta utan meddelanden",
      "Fånga startläget utan att klicka frågor eller skicka ett AI-meddelande",
    ],
    scenario: "S1",
    visual: { variant: 5, focus: "assistant" },
    use: ["pages/Plattform.tsx"],
    text: {
      sv: {
        alt: "Fråga CAIRE med fem schemafrågor och tom skrivruta för vald lösning",
        shows:
          "Den inbyggda assistenten visar schemafrågor om förbättringar, topptimmar, otilldelade besök, datakorrigering och omkörning tillsammans med arbetskontexten.",
        proves:
          "Planeraren kan välja en relevant startfråga för det öppna schemat och lösningen. Startläget visar frågeval, inte ett AI-svar, förslag eller genomförd förbättring.",
      },
      en: {
        alt: "Ask CAIRE with five schedule prompts and an empty composer for the selected solution",
        shows:
          "The native assistant presents prompts about improvements, peak hours, unassigned visits, data fixes and reruns alongside the work context.",
        proves:
          "The planner can choose a relevant starting question for the open schedule and solution. This initial state shows prompt choices, not an AI answer, proposal or completed improvement.",
      },
    },
  },
];
